import { useState, useMemo, useCallback } from "react";
import { PeopleIndex, Person } from "../types/person";

export function useAncestorTree(
  people: PeopleIndex,
  rootId: string,
  defaultDepth = 2
) {
  const [expansionPath, setExpansionPath] = useState<string[]>([]);

  const generations: Person[][] = useMemo(() => {
    const cols: Person[][] = [];
    const root = people[rootId];
    const spouse = root.spouseId ? people[root.spouseId] : undefined;

    // 0: root + spouse
    cols.push(spouse ? [root, spouse] : [root]);

    // 1: parents (root & spouse)
    const parentIds = [
      ...(root.parentIds ?? []),
      ...(spouse?.parentIds ?? []),
    ].filter((pid): pid is string => pid !== undefined);
    cols.push(parentIds.map((pid) => people[pid]).filter(Boolean));

    // 2: grandparents
    const grandparents = cols[1]
      .flatMap((p) => p.parentIds ?? [])
      .filter((pid): pid is string => pid !== undefined)
      .map((pid) => people[pid])
      .filter(Boolean);
    cols.push(grandparents);

    // additional drilled generations
    expansionPath.forEach((id) => {
      const p = people[id];
      if (!p?.parentIds) return;
      const parents = p.parentIds
        .filter((pid): pid is string => pid !== undefined)
        .map((pid) => people[pid])
        .filter(Boolean);
      cols.push(parents);
      const gparents = parents
        .flatMap((par) => par.parentIds ?? [])
        .filter((pid): pid is string => pid !== undefined)
        .map((pid) => people[pid])
        .filter(Boolean);
      cols.push(gparents);
    });

    return cols;
  }, [people, rootId, expansionPath, defaultDepth]);

  const handleSelect = useCallback(
    (gen: number) => {
      if (gen <= defaultDepth) {
        setExpansionPath([]);
      } else {
        const expIndex = Math.floor((gen - (defaultDepth + 1)) / 2);
        setExpansionPath((prev) => prev.slice(0, expIndex + 1));
      }
    },
    [defaultDepth]
  );

  const handleExpand = useCallback(
    (personId: string, gen: number) => {
      const person = people[personId];
      if (!person?.parentIds?.length) return;
      let newPath: string[];
      if (gen === defaultDepth) {
        newPath = [personId];
      } else {
        const expIndex = Math.floor((gen - (defaultDepth + 1)) / 2);
        newPath = [...expansionPath.slice(0, expIndex), personId];
      }
      setExpansionPath(newPath);
    },
    [people, expansionPath, defaultDepth]
  );

  return { generations, handleSelect, handleExpand };
}
